/**
 * Orders module — activity derivation for the ActivityAvatar component.
 *
 * Contract (Coolman Design System):
 *   Every module that renders <ActivityAvatar/> owns its own
 *   `getLastActivity(record)` derivation function. The Avatar itself is
 *   presentation-only and never inspects domain shapes.
 *
 *   `getLastActivity(record) → { action, name, at }`
 *     action ∈ Created | Sent | Edited | Re-Sent | Cancelled | Completed | Archived
 *     name    — full name of the responsible employee (fallbacks: created_by → "—")
 *     at      — ISO timestamp of that action
 *
 * Sibling modules will each publish their own `getLastActivity` from
 * `/app/frontend/src/lib/<module>/activity.js` (workforce, pricing,
 * inventory, fleet, customers, tasks, administration).
 *
 * Order-specific derivation rules (pure function over the Order document):
 *   Cancelled status                → "Cancelled" at updated_at | cancelled_at
 *   sent_at set + revisions >= 1    → "Re-Sent"   at sent_at
 *   sent_at set                     → "Sent"      at sent_at
 *   revisions >= 1                  → "Edited"    at last revision.timestamp
 *   otherwise                       → "Created"   at created_at
 */

export function getLastActivity(order) {
  const revs = (order && order.revisions) || [];
  let action = "Created";
  let name = (order && order.created_by) || "—";
  let at = order && order.created_at;

  if (revs.length > 0) {
    const last = revs[revs.length - 1];
    action = "Edited";
    name = last.by || last.updated_by || last.author || name;
    at   = last.at || last.timestamp || last.updated_at || at;
  }
  if (order && order.sent_at) {
    action = revs.length >= 1 ? "Re-Sent" : "Sent";
    name = order.sent_by || order.created_by || name;
    at   = order.sent_at;
  }
  if (order && order.status === "Cancelled") {
    action = "Cancelled";
    name = order.cancelled_by || order.updated_by || order.created_by || name;
    at   = order.updated_at || order.cancelled_at || at;
  }
  return { action, name, at };
}
